/* eslint-disable  @typescript-eslint/no-explicit-any */
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import Cookies from 'js-cookie';
import { Button, InputText, Loading } from '../../components';
import InputMasked from '../../components/InputMasked';
import InputPasswordRules from '../../components/InputPasswordRules';
import Popup from '../../components/Popup';
import RecaptchaContext from '../../hooks/RecaptchaContext';
import * as configProvider from '../../services/ConfigProvider';
import {
  GoogleAnalyticsConstants,
  pushPageViewDataLayer,
  sendErrorToGa,
} from '../../services/googleTagManager';
import { createUser, userLogin } from '../../services/Login';
import checkAutofill from '../../utils/helper/check-autofill';
import redirectHistory from '../../utils/redirect';
import useTranslation from '../../utils/translation';
import AccountExists from './AccountExists';
import {
  birthdayValidator,
  cellphoneValidator,
  cpfValidator,
  emailValidator,
  fullnameValidator,
  passwordValidator,
} from './FieldsValidator';
import styles from './styles.module.css';
import { CreateAccountResponse } from './types';
import { onlyNumbers } from '../../utils/helper/sanitization';

interface CustomerNatural {
  fullname: string;
  cellphone: string;
  cpf: string;
  email: string;
  birthday: string;
  password: string;
  preferences: string;
  newsletter: boolean;
}

const CustomerNaturalDefault = {
  fullname: '',
  cellphone: '',
  cpf: '',
  email: '',
  birthday: '',
  password: '',
  preferences: '',
  newsletter: true,
};

const CreateAccountBRNatural = () => {
  const config = configProvider.useConfig();
  const API_URL = configProvider.configGetLoginAdapterUrl(config);
  const localRedirect = configProvider.configGetAliceHost(config);
  const recaptchaPublicKey = configProvider.configGetRecaptchaKey(config);

  let isPhoneEnabled = configProvider.configGetPhoneEnabled(config) === 'true';

  // Override the phone enabled env if the cookie is set
  const customerPhoneEnabledCookie = Cookies.get(
    GoogleAnalyticsConstants.CUSTOMER_PHONE_ENABLED,
  );
  if (customerPhoneEnabledCookie !== undefined) {
    isPhoneEnabled = customerPhoneEnabledCookie === 'ligado';
  }

  let isPhoneRequired =
    configProvider.configGetPhoneRequired(config) === 'true';

  // Override the phone required env if the cookie is set
  const customerPhoneRequiredCookie = Cookies.get(
    GoogleAnalyticsConstants.CUSTOMER_PHONE_REQUIRED,
  );
  if (customerPhoneRequiredCookie !== undefined) {
    isPhoneRequired = customerPhoneRequiredCookie === 'ligado';
  }

  const [values, setValues] = useState<CustomerNatural>(CustomerNaturalDefault);
  const [errors, setErrors] = useState<CustomerNatural>(CustomerNaturalDefault);
  const [createUserResponse, setCreateUserResponse] = useState<
    CreateAccountResponse | undefined
  >();
  const [isValid, setIsValid] = useState(false);
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const translate = useTranslation('BR');

  const recaptcha = useContext(RecaptchaContext);

  const renewRecaptchaToken = async () => {
    const token = await recaptcha.executeReCaptcha(recaptchaPublicKey);
    return token;
  };

  const togglePopup = () => {
    setIsPopupOpen(!isPopupOpen);
  };

  useEffect(() => {
    pushPageViewDataLayer('login - create', 'Registration BR Natural');

    setTimeout(() => {
      checkAutofill('styles_active__OmSYD');
    }, 1000);
  }, []);

  const handleCreateAccount = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    let isOk = true;
    for (const [key, value] of Object.entries(values)) {
      if (key != '' && validateFormFields(key, value)) {
        isOk = false;
      }
    }

    if (isOk) {
      const [day, month, year] = values.birthday.split('/');
      const fullnameTrim = values.fullname.trim().replace(/\s\s+/g, ' ');
      const user = {
        lastName: fullnameTrim.split(' ').pop()?.toString() || '',
        firstName: fullnameTrim.substring(0, fullnameTrim.lastIndexOf(' ')),
        birthday: year + '-' + month + '-' + day,
        email: values.email,
        isNewsLetterSubscribed: values.newsletter.toString(),
        password: values.password,
        taxIdentification: values.cpf,
        phone: onlyNumbers(values.cellphone),
        gender:
          values.preferences === translate['login-registration-female-style']
            ? 'female'
            : 'male',
      };
      setLoading(true);
      try {
        await createUser(API_URL, user);

        const newRecaptchaToken = await renewRecaptchaToken();

        const loginResponse = await userLogin(
          API_URL,
          values.email,
          values.password,
          newRecaptchaToken,
        );

        redirectHistory(localRedirect, loginResponse.access_token);
      } catch (e: any) {
        if (e.cause && e.cause.type) {
          const response = { ...e.cause } as CreateAccountResponse;
          setCreateUserResponse(response);
        } else {
          console.log('um erro ocorreu', e);
          setCreateUserResponse(undefined);
        }
        setLoading(false);
        togglePopup();
      }
    }
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setValues({
      ...values,
      [event.target.name]:
        event.target.type === 'checkbox'
          ? event.target.checked
          : event.target.value,
    });
  };

  const handleInputSetValue = (inputName: string, value: string) => {
    setValues({
      ...values,
      [inputName]: value,
    });
  };

  function setFieldError(field: string, error: string | null) {
    error = error === null ? '' : error;
    setErrors({
      ...errors,
      [field]: error,
    });
  }

  const handleFieldsValidate = (e: React.ChangeEvent<HTMLInputElement>) => {
    const fieldata = e.target.value;

    const error = validateFormFields(e.target.name, fieldata);
    const terror = error ? translate[error] : '';
    setFieldError(e.target.name, terror);
    if (error) {
      sendErrorToGa(
        'login',
        'registration natural',
        e.target.name + ': ' + error,
      );
    }
  };

  const validateFormFields = useCallback(
    (name: string, fieldata: string) => {
      if (name === 'fullname') {
        return fullnameValidator(fieldata);
      }
      if (name === 'cellphone') {
        return cellphoneValidator(
          fieldata,
          'BR',
          isPhoneEnabled,
          isPhoneRequired,
        );
      }
      if (name === 'cpf') {
        return cpfValidator(fieldata);
      }
      if (name === 'email') {
        return emailValidator(fieldata);
      }
      if (name === 'birthday') {
        return birthdayValidator(fieldata);
      }
      if (name === 'password') {
        return passwordValidator(fieldata);
      }
      if (name === 'preferences' && fieldata.length == 0) {
        return 'login-registration-check-preferences';
      }
      return '';
    },
    [isPhoneEnabled, isPhoneRequired],
  );

  useMemo(() => {
    if (values === null) return setIsValid(false);
    for (const [key, value] of Object.entries(values)) {
      const error = validateFormFields(key, value);
      if (error.length > 0) {
        setIsValid(false);
        return;
      }
    }
    setIsValid(true);
  }, [values, validateFormFields]);

  return (
    <>
      <Loading visibled={loading} />
      <Popup
        PopupContent={() => (
          <AccountExists createUserResponse={createUserResponse} />
        )}
        isOpen={isPopupOpen}
        onClose={togglePopup}
      />
      <form
        className={styles.formNatural}
        autoComplete="off"
        onSubmit={handleCreateAccount}
      >
        <InputText
          placeholder={translate['login-registration-fullname']}
          name="fullname"
          type="text"
          value={values.fullname}
          error={errors.fullname.length > 0}
          textError={errors.fullname}
          required={true}
          onBlur={handleFieldsValidate}
          onChange={handleChange}
        />

        <InputMasked
          placeholder={translate['login-registration-cpf']}
          name="cpf"
          value={values.cpf}
          inputMode="numeric"
          mask="999.999.999-99"
          error={errors.cpf.length > 0}
          textError={errors.cpf}
          onChange={handleChange}
          onBlur={handleFieldsValidate}
        />

        <InputMasked
          placeholder={translate['login-registration-cellphone']}
          name="cellphone"
          value={values.cellphone}
          inputMode="numeric"
          mask="(99) 99999-9999"
          error={errors.cellphone.length > 0}
          textError={errors.cellphone}
          hide={!isPhoneEnabled}
          onChange={handleChange}
          onBlur={handleFieldsValidate}
        />

        <InputText
          placeholder={translate['login-registration-email']}
          name="email"
          type="email"
          value={values.email}
          setValue={(value) => handleInputSetValue('email', value)}
          error={errors.email.length > 0}
          textError={errors.email}
          required={true}
          onBlur={handleFieldsValidate}
          onChange={handleChange}
        />

        <InputMasked
          placeholder={translate['login-registration-birth']}
          name="birthday"
          value={values.birthday}
          inputMode="numeric"
          mask="99/99/9999"
          error={errors.birthday.length > 0}
          textError={errors.birthday}
          onChange={handleChange}
          onBlur={handleFieldsValidate}
        />

        <InputPasswordRules
          country="BR"
          inputPassValue={values.password}
          setInputPassValue={(value) => handleInputSetValue('password', value)}
          onBlur={handleFieldsValidate}
          autoComplete="new-password"
        />

        <InputText
          placeholder={translate['login-registration-store-pref']}
          name="preferences"
          type="select"
          value={values.preferences}
          error={errors.preferences.length > 0}
          textError={errors.preferences}
          required={true}
          setValue={(value) => handleInputSetValue('preferences', value)}
          onChange={handleChange}
          onBlur={handleFieldsValidate}
          items={[
            translate['login-registration-female-style'],
            translate['login-registration-male-style'],
          ]}
        />

        <div className={styles.acceptNews}>
          <label>
            <input
              type="checkbox"
              name="newsletter"
              checked={values.newsletter}
              value={values.newsletter.toString()}
              onChange={handleChange}
            />
            <span>{translate['login-registration-accept-newsletter']}</span>
          </label>
        </div>

        <div className={styles.acceptRegistration}>
          <span
            className={styles.acceptRegistration}
            dangerouslySetInnerHTML={{
              __html: translate['login-registration-accept-registration'],
            }}
          />
        </div>

        <Button
          id={styles.createAccount}
          text={translate['login-manual-button-create-account']}
          type="submit"
          disabled={!isValid}
        />

        <div className={styles.backLogin}>
          {translate['login-registration-have-account']}
          &nbsp;
          <Link to={'/'}>{translate['login-registration-click-enter']}</Link>
        </div>
      </form>
    </>
  );
};

export default CreateAccountBRNatural;
